import React, { useState, useMemo, useEffect } from "react";
import { T, AWARD_COLORS, fmt } from "./theme";
import { SectionHeader, Card, Avatar, Btn, AwardBadge, Input } from "./ui";
import { calcPontosCorretor, calcAwardVitalicio } from "./awards";
import { AWARDS } from "./data";
import ConfigCorretor from "./ConfigCorretor";
import Financeiro from "./Financeiro";
import { supabase, isSupabaseReady } from "./supabase";

// ═══════════════════════════════════════════════════════════════════════
// CORRETOR DETALHE (admin) — 4 abas: Financeiro · Ranking · Configurações · Árvore
// ═══════════════════════════════════════════════════════════════════════

export default function CorretorDetalhe({ corretor, corretores, times, currentUser, setUser, setCorretores, onVoltar }) {
  const [tab, setTab] = useState("financeiro");

  const c = useMemo(
    () => corretores.find(x => x.id === corretor.id || x._uuid === corretor._uuid) || corretor,
    [corretores, corretor]
  );

  if (!c) return null;

  const award = calcAwardVitalicio(c);
  const pts = calcPontosCorretor(c);
  const initials = (c.nome || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const ac = AWARD_COLORS[award.id] || {};
  const time = c.timeId && times ? times.find(t => t.id === c.timeId) : null;

  // Sintetiza currentUser virtual pra Financeiro/ConfigCorretor olharem ESSE corretor
  const corretorVirtualUser = {
    ...currentUser,
    role: "corretor",
    _corretorUuid: c._uuid || c.id,
    corretorId: c.id,
    username: c.username || c.nome,
  };

  const TABS = [
    { id: "financeiro", label: "💰 Financeiro" },
    { id: "ranking",    label: "🏆 Ranking" },
    { id: "arvore",     label: "🌳 Árvore" },
    { id: "config",     label: "⚙️ Configurações" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Btn variant="ghost" onClick={onVoltar} style={{ alignSelf: "flex-start", fontSize: 11 }}>
        ← Voltar à lista
      </Btn>

      {/* Cabeçalho do corretor */}
      <Card style={{
        padding: 24,
        background: ac.bg || T.cardSoft,
        border: `1px solid ${ac.border || T.border}`,
      }}>
        <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
          <Avatar initials={initials} size={72} tone={ac.fg || T.brand} photo={c.foto || c.foto_url} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: T.text }}>{c.nome}</div>
            <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>
              📍 {c.cidade}-{c.uf}
              {time && <span> · {time.emoji} {time.nome}</span>}
              {c.email && <span> · ✉️ {c.email}</span>}
              {c.telefone && <span> · 📞 {c.telefone}</span>}
            </div>
            {c.tagUnica && (
              <div style={{ fontSize: 11, color: T.brand, marginTop: 4, fontWeight: 600 }}>🔗 {c.tagUnica}</div>
            )}
          </div>
          <AwardBadge awardId={award.id} size="md" />
        </div>
      </Card>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "10px 16px", borderRadius: T.r.md,
            background: tab === t.id ? T.brand + "22" : T.card,
            border: `1px solid ${tab === t.id ? T.brand : T.border}`,
            color: tab === t.id ? T.brand : T.textSec,
            cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
          }}>{t.label}</button>
        ))}
      </div>

      {/* Conteúdo */}
      {tab === "financeiro" && (
        <Financeiro corretores={corretores} currentUser={corretorVirtualUser} />
      )}

      {tab === "ranking" && (currentUser?.role === "proprietario" || currentUser?.role === "admin" || currentUser?.role === "admin_master" || currentUser?.role === "comercial") && (
        <AtribuirPontos corretor={c} setCorretores={setCorretores} />
      )}
      {tab === "ranking" && (
        <RankingDoCorretor corretor={c} pts={pts} award={award} />
      )}
      {tab === "ranking" && <BonusTargetsBlock corretorUuid={c._uuid || c.id} />}
      {tab === "ranking" && <PremiosFisicosBlock corretor={c} /> }

      {tab === "config" && (
        <ConfigCorretor
          currentUser={currentUser}
          corretores={corretores}
          times={times}
          setUser={setUser}
          setCorretores={setCorretores}
          targetCorretorId={c.id}
          embedded
        />
      )}

      {tab === "arvore" && (
        <ArvoreDoCorretor corretor={c} corretores={corretores} />
      )}
    </div>
  );
}

// ═══ Ranking do corretor: stats principais ═══════════════════════════
function RankingDoCorretor({ corretor: c, pts, award }) {
  const ac = AWARD_COLORS[award.id] || {};
  const awIdx = AWARDS.findIndex(a => a.id === award.id);
  const next = awIdx >= 0 && awIdx < AWARDS.length - 1 ? AWARDS[awIdx + 1] : null;
  const progresso = next ? Math.min(100, Math.round((pts.total / next.pts) * 100)) : 100;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
          🏆 Award e pontuação
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
          <Stat label="Award atual" valor={award.nome} isText cor={ac.fg} />
          <Stat label="Pontos totais" valor={pts.total} />
          <Stat label="Pts por vendas" valor={pts.pVendas || 0} />
          <Stat label="Pts por ativação rede" valor={pts.pAtivados || 0} />
        </div>
        {next && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textSub, marginBottom: 4 }}>
              <span>{award.nome}</span>
              <span>{next.nome} ({next.pts} pts)</span>
            </div>
            <div style={{ height: 6, background: T.elev, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${progresso}%`, height: "100%", background: ac.fg || T.brand }} />
            </div>
            <div style={{ textAlign: "center", marginTop: 6, fontSize: 11, color: T.textDim }}>
              {progresso}% — faltam {next.pts - pts.total} pontos
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
          💰 Volume e vendas
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
          <Stat label="Vendas diretas" valor={c.vendasDiretas || 0} />
          <Stat label="Volume total" valor={fmt.brl(c.vendasValor || 0)} isText />
          <Stat label="Volume ano" valor={fmt.brl(c.vendasValorAno || 0)} isText />
          <Stat label="Volume mês" valor={fmt.brl(c.vendasValorMes || 0)} isText />
          <Stat label="Volume semana" valor={fmt.brl(c.vendasValorSemana || 0)} isText />
        </div>
      </Card>

      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
          🌳 Rede comercial
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
          <Stat label="Cadastrados" valor={c.cadastrados || 0} />
          <Stat label="Ativados" valor={c.ativados || 0} />
          <Stat label="Pts semana" valor={c.pontosSemana || 0} />
          <Stat label="Pts mês" valor={c.pontosMes || 0} />
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, valor, isText, cor }) {
  return (
    <div style={{
      background: T.cardSoft, border: `1px solid ${T.border}`,
      borderRadius: T.r.md, padding: "12px 14px",
    }}>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div className="serif" style={{ fontSize: isText ? 14 : 22, fontWeight: 700, color: cor || T.text }}>{valor}</div>
    </div>
  );
}

// ═══ Árvore do corretor: lista filhos/netos do nó ═══════════════════
function ArvoreDoCorretor({ corretor: c, corretores }) {
  const mapa = useMemo(() => {
    const m = {};
    corretores.forEach(x => {
      const pid = x.indicadoPor || x.indicado_por || null;
      if (!m[pid]) m[pid] = [];
      m[pid].push(x);
    });
    return m;
  }, [corretores]);

  const meuId = c.id;
  const meuUuid = c._uuid || c.id;
  const filhos = (mapa[meuId] || []).concat(mapa[meuUuid] || []);

  function descendentesAte(id, uuid, nivel = 1, max = 5, acc = []) {
    if (nivel > max) return acc;
    const lvl = (mapa[id] || []).concat(mapa[uuid] || []);
    lvl.forEach(f => {
      acc.push({ ...f, _nivel: nivel });
      descendentesAte(f.id, f._uuid, nivel + 1, max, acc);
    });
    return acc;
  }

  const todos = descendentesAte(meuId, meuUuid, 1, 10);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
            🌳 Rede abaixo
          </div>
          <div className="serif" style={{ fontSize: 24, fontWeight: 700, color: T.brand }}>
            {todos.length}
          </div>
        </div>
        <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>
          {filhos.length} indicados diretos · {todos.length - filhos.length} indiretos
        </div>
      </Card>

      {todos.length === 0 && (
        <div style={{ textAlign: "center", padding: 30, color: T.textDim }}>
          Nenhum corretor indicado ainda.
        </div>
      )}

      {todos.map(f => {
        const aw = calcAwardVitalicio(f);
        const ac = AWARD_COLORS[aw.id] || {};
        const ini = (f.nome || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
        return (
          <div key={f.id || f._uuid} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "10px 14px", marginLeft: (f._nivel - 1) * 18,
            background: T.card, border: `1px solid ${T.border}`,
            borderLeft: `3px solid ${ac.fg || T.brand}`,
            borderRadius: T.r.md,
          }}>
            <span style={{
              fontSize: 9, padding: "2px 6px", borderRadius: 4,
              background: T.brand + "22", color: T.brand, fontWeight: 700,
            }}>N{f._nivel}</span>
            <Avatar initials={ini} size={32} photo={f.foto || f.foto_url} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{f.nome}</div>
              <div style={{ fontSize: 10, color: T.textDim }}>{f.cidade}-{f.uf} · {f.vendasDiretas || 0} vendas</div>
            </div>
            <AwardBadge awardId={aw.id} size="sm" />
          </div>
        );
      })}
    </div>
  );
}

// ═══ Atribuir pontos manuais (só proprietário) ═══════════════════════
function AtribuirPontos({ corretor, setCorretores }) {
  const [delta, setDelta] = useState("");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);

  const atual = corretor.pontosBonus || 0;

  async function aplicar() {
    setMsg(null);
    const n = parseInt(delta, 10);
    if (isNaN(n) || n === 0) { setMsg({ tipo: "err", texto: "Informe quantos pontos somar (use - pra subtrair)." }); return; }
    if (!isSupabaseReady) return;
    setSalvando(true);
    try {
      const novoTotal = atual + n;
      const { error } = await supabase.from("corretores")
        .update({ pontos_bonus: novoTotal })
        .eq("id", corretor._uuid || corretor.id);
      if (error) throw error;
      if (setCorretores) {
        setCorretores(prev => prev.map(x =>
          (x._uuid === corretor._uuid || x.id === corretor.id)
            ? { ...x, pontosBonus: novoTotal }
            : x
        ));
      }
      setMsg({ tipo: "ok", texto: `✅ ${n > 0 ? `+${n}` : n} pontos · ${motivo || "(sem motivo)"}` });
      setDelta(""); setMotivo("");
    } catch (e) {
      setMsg({ tipo: "err", texto: "Erro: " + e.message });
    } finally { setSalvando(false); }
  }

  return (
    <Card style={{ borderColor: T.gold + "44" }}>
      <div style={{ fontSize: 11, color: T.gold, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
        ⭐ Atribuir pontos manuais (treinamentos, desafios)
      </div>
      <div style={{ fontSize: 11, color: T.textSub, marginBottom: 10 }}>
        Bônus atual: <strong style={{ color: T.gold }}>{atual} pts</strong> · somam aos pontos do corretor
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "100px 1fr auto", gap: 8, alignItems: "end" }}>
        <Input label="Pontos (+/-)" type="number" value={delta} onChange={e => setDelta(e.target.value)} placeholder="+25" />
        <Input label="Motivo (opcional)" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex: desafio treinamento abril" />
        <Btn onClick={aplicar} disabled={salvando}>{salvando ? "..." : "✨ Aplicar"}</Btn>
      </div>
      {msg && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: T.r.md,
          background: msg.tipo === "ok" ? T.ok + "15" : T.err + "15",
          border: `1px solid ${msg.tipo === "ok" ? T.ok : T.err}44`,
          color: msg.tipo === "ok" ? T.ok : T.err, fontSize: 12,
        }}>{msg.texto}</div>
      )}
    </Card>
  );
}

// ═══ Bônus Targets (admin vê do corretor) ════════════════════════════
function BonusTargetsBlock({ corretorUuid }) {
  const [comissoes, setComissoes] = useState([]);

  useEffect(() => {
    if (!corretorUuid || !isSupabaseReady) return;
    (async () => {
      const { data } = await supabase.from("comissoes").select("*").eq("corretor_id", corretorUuid);
      setComissoes(data || []);
    })();
  }, [corretorUuid]);

  const agora = new Date();
  const umAno = new Date(agora); umAno.setFullYear(agora.getFullYear() - 1);
  const noventa = new Date(agora); noventa.setDate(agora.getDate() - 90);
  const inicioAno = new Date(agora.getFullYear(), 0, 1);

  const redeAno = comissoes.filter(c => (c.nivel ?? 0) > 0 && new Date(c.criado_em) >= umAno)
    .reduce((s, c) => s + parseFloat(c.comissao_valor ?? c.valor ?? 0), 0);
  const volume90 = comissoes.filter(c => (c.nivel ?? 0) === 0 && new Date(c.criado_em) >= noventa)
    .reduce((s, c) => s + parseFloat(c.valor_venda ?? c.valor_comissionavel ?? 0), 0);
  const vendasAno = comissoes.filter(c => (c.nivel ?? 0) === 0 && new Date(c.criado_em) >= inicioAno)
    .reduce((s, c) => s + parseFloat(c.valor_venda ?? c.valor_comissionavel ?? 0), 0);

  const targets = [
    { id: "rede",     titulo: "Desempenho de rede",     alvo: 10000,   atual: redeAno,   bonus: 1000, label: "R$ 10k em comissão de rede (último ano)" },
    { id: "melhoria", titulo: "Melhoria de desempenho", alvo: 1000000, atual: volume90,  bonus: 2000, label: "R$ 1M em volume (últimos 90 dias)" },
    { id: "anual",    titulo: "Desempenho anual",       alvo: 4000000, atual: vendasAno, bonus: 3000, label: "R$ 4M em vendas no ano" },
  ];

  return (
    <Card>
      <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
        🎯 Bônus de Targets
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {targets.map(t => {
          const pct = Math.min(100, (t.atual / t.alvo) * 100);
          const ok = t.atual >= t.alvo;
          return (
            <div key={t.id} style={{
              padding: "10px 12px", borderRadius: T.r.md,
              background: ok ? T.ok + "15" : T.cardSoft,
              border: `1px solid ${ok ? T.ok + "44" : T.border}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{ok && "✅ "}{t.titulo}</div>
                  <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>{t.label}</div>
                </div>
                <div className="serif" style={{ fontSize: 18, fontWeight: 700, color: ok ? T.ok : T.gold }}>+{fmt.brl(t.bonus)}</div>
              </div>
              <div style={{ marginTop: 6, fontSize: 10, color: T.textSub, display: "flex", justifyContent: "space-between" }}>
                <span>{fmt.brl(t.atual)} de {fmt.brl(t.alvo)}</span>
                <span style={{ color: ok ? T.ok : T.brand, fontWeight: 600 }}>{pct.toFixed(0)}%</span>
              </div>
              <div style={{ marginTop: 4, height: 4, background: T.elev, borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: ok ? T.ok : T.brand, transition: "width 320ms" }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ═══ Prêmios Físicos (barra contínua com marcos 2M/3M/5M) ════════════
function PremiosFisicosBlock({ corretor }) {
  const vendasAno = parseFloat(corretor?.vendasValorAno ?? corretor?.vendasValor ?? 0);
  const MAX = 5000000;
  const marcos = [
    { id: "iphone",   emoji: "📱", nome: "iPhone do ano",                 award: "Golden",   vol: 2000000, cor: "#d7be7c" },
    { id: "cruzeiro", emoji: "🚢", nome: "Cruzeiro de luxo (2p)",         award: "Platinum", vol: 3000000, cor: "#b9c5d6" },
    { id: "byd",      emoji: "🚗", nome: "BYD 100% elétrico",             award: "Diamond",  vol: 5000000, cor: "#9bd2e6" },
  ];
  const pctTotal = Math.min(100, (vendasAno / MAX) * 100);

  return (
    <Card>
      <style>{`
        @keyframes premioConquistado {
          0%   { transform: scale(1); filter: drop-shadow(0 0 0 transparent); }
          50%  { transform: scale(1.25) rotate(-5deg); filter: drop-shadow(0 0 16px currentColor); }
          100% { transform: scale(1.08); filter: drop-shadow(0 0 8px currentColor); }
        }
        @keyframes premioPulse {
          0%, 100% { box-shadow: 0 0 0 0 transparent; }
          50%      { box-shadow: 0 0 0 8px rgba(215,190,124,0.25); }
        }
        .premio-conquistado { animation: premioConquistado 1.2s ease-out forwards, premioPulse 2.4s ease-in-out infinite 1.2s; }
        @keyframes barraShine {
          0%   { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
        .barra-progresso-shine {
          background: linear-gradient(90deg, #d7be7c 0%, #f5e1a8 50%, #d7be7c 100%);
          background-size: 200% 100%;
          animation: barraShine 3s linear infinite;
        }
      `}</style>

      <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
        🎁 Prêmios Anuais
      </div>

      {/* Resumo numérico */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: T.gold }}>
          {fmt.brl(vendasAno)}
        </div>
        <div style={{ fontSize: 11, color: T.textSub }}>
          vendas no ano · meta máxima {fmt.brl(MAX)}
        </div>
      </div>

      {/* Barra contínua com marcos */}
      <div style={{ position: "relative", height: 64, marginBottom: 18 }}>
        {/* Trilho */}
        <div style={{
          position: "absolute", left: 0, right: 0, top: 28,
          height: 8, background: T.elev, borderRadius: 4,
        }} />
        {/* Preenchimento */}
        <div
          className={pctTotal > 0 ? "barra-progresso-shine" : ""}
          style={{
            position: "absolute", left: 0, top: 28,
            width: `${pctTotal}%`, height: 8, borderRadius: 4,
            transition: "width 800ms cubic-bezier(.2,.7,.3,1.2)",
          }}
        />
        {/* Marcos */}
        {marcos.map(m => {
          const pos = (m.vol / MAX) * 100;
          const ok = vendasAno >= m.vol;
          return (
            <div key={m.id} style={{
              position: "absolute", left: `${pos}%`, top: 0,
              transform: "translateX(-50%)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              color: m.cor,
            }}>
              <div
                className={ok ? "premio-conquistado" : ""}
                style={{
                  fontSize: 28, lineHeight: 1,
                  filter: ok ? `drop-shadow(0 0 8px ${m.cor})` : "grayscale(60%) opacity(0.5)",
                  transition: "filter 400ms",
                }}
              >{m.emoji}</div>
              <div style={{
                width: 2, height: 14, background: ok ? m.cor : T.borderMid,
                marginTop: 2,
              }} />
              <div style={{
                fontSize: 9, color: ok ? m.cor : T.textDim, fontWeight: 700,
                whiteSpace: "nowrap", marginTop: 2,
              }}>
                {fmt.brl(m.vol).replace("R$", "R$ ").replace(",00", "")}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legenda das conquistas */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 36 }}>
        {marcos.map(m => {
          const ok = vendasAno >= m.vol;
          const falta = Math.max(0, m.vol - vendasAno);
          return (
            <div key={m.id} style={{
              padding: "8px 10px", borderRadius: T.r.md,
              background: ok ? m.cor + "22" : T.cardSoft,
              border: `1px solid ${ok ? m.cor + "66" : T.border}`,
              fontSize: 10,
            }}>
              <div style={{ color: m.cor, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {m.emoji} {m.award}
              </div>
              <div style={{ color: T.text, marginTop: 2, fontWeight: 600 }}>{m.nome}</div>
              <div style={{ color: ok ? T.ok : T.textDim, marginTop: 2 }}>
                {ok ? "🏆 Conquistado!" : `Faltam ${fmt.brl(falta)}`}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
